import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getLaboklinApi } from '../../services/laboklinApi';
import type { LabReportSummary } from '../../types/laboklin';

/**
 * The reports table, as the way into a workup.
 *
 * There is no "Ask GekkoVet" action and no GekkoVet column: opening a report starts
 * the interpretation, so there is nothing to ask for here. The wireframe showed a
 * status pill per row - "Interpreted · 2 follow-on tests" - which needs the result
 * stored per report; nothing is stored, so the column would be a promise the page
 * cannot keep.
 */

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}.${m}.${y}` : iso;
}

export const ReportsListView: React.FC = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<LabReportSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    getLaboklinApi()
      .listReports()
      .then((data) => {
        if (!cancelled) setReports(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Paper sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Paper>
    );
  }

  if (error) {
    return <Alert severity="error">Could not load reports from Laboklin: {error}</Alert>;
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button variant="outlined" color="primary" onClick={() => navigate('/mylab2/order/new')}>
          New online order
        </Button>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Report no.</TableCell>
              <TableCell>Report date</TableCell>
              <TableCell>Species</TableCell>
              <TableCell>Breed</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.map((r) => (
              <TableRow
                key={r.reportNo}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/mylab2/reports/${r.reportNo}`)}
              >
                <TableCell>
                  <Link underline="hover">{r.reportNo}</Link>
                </TableCell>
                <TableCell>{formatDate(r.reportDate)}</TableCell>
                <TableCell>{r.species ?? ''}</TableCell>
                <TableCell>{r.breed ?? ''}</TableCell>
                <TableCell sx={{ color: '#2e7d32' }}>{r.reportType ?? ''}</TableCell>
                <TableCell align="right">
                  <Link underline="hover">Open</Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Typography sx={{ fontSize: 12, color: 'text.secondary', px: 1, pt: 1.5 }}>
        Opening a report interprets it. The interpretation runs while you read the values.
      </Typography>
    </Paper>
  );
};

export default ReportsListView;
